import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {createCurveGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {CurveGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode, curveGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {CurveGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

// Single-variant operator (curve-only): parameter type is concrete, no per-dim genericity.
export type FlattenOperatorParameters = {
    name: string
    attribute: CurveGeometryAttributeLikeNode | null
}

export const flattenOperatorParametersSchema = z.object({
    name: z.string(),
    attribute: z.nullable(curveGeometryAttributeLike),
})

function applyFlattenOperator(attribute: CurveGeometryAttributeData): CurveGeometryAttributeData {
    const attrRefData = attribute.ref
    const flattenedAttrRefData = applyGeometryOperator((attRef: AttributeRef) => attRef.flatten(), attrRefData)
    return createCurveGeometryAttributeData(flattenedAttrRefData)
}

export abstract class GeometryFlattenOperatorNode<out P extends FlattenOperatorParameters = FlattenOperatorParameters> extends GeometryAttributeNode<1, P> {
    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return parameters.attribute !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<CurveGeometryAttributeData | null>; invalidator?: BuilderInlet<null>} {
        const {attribute} = parameters

        const [evaluatedAttributeValid, evaluatedAttributeInvalid] = scope.branch(evaluator.evaluate(attribute))
        const geometryAttributeData = scope.pureLambda(evaluatedAttributeValid, (attributeData) => applyFlattenOperator(attributeData), "applyFlattenOperator")

        return {evaluatedGeometryData: geometryAttributeData, invalidator: evaluatedAttributeInvalid}
    }
}
